import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { sortByEndDateDesc, sortNestedProjectsByEndDateDesc } from "../../../utils/sortByDate";
import { HiLink, HiBadgeCheck } from "react-icons/hi";
import Tooltip from '../../shared/Tooltip';
import { RichTextContent } from '../../../utils/richTextProcessor';
import { createProfileViewRequestAction } from '../../../redux/actions/userActions';
import { getSentProfileViewRequestsAction } from '../../../redux/actions/profileViewRequestActions';
import { useToast } from '../../../contexts/ToastContext';

const EmploymentTab = ({
  isNHSExpanded,
  setIsNHSExpanded,
  isOpenAIExpanded,
  setIsOpenAIExpanded,
  isUnitedHealthExpanded,
  setIsUnitedHealthExpanded,
  selectedExperience,
  setSelectedExperience,
  isExperienceCardExpanded,
  setIsExperienceCardExpanded,
  isDescriptionVisible,
  setIsDescriptionVisible,
  setVerificationOverlay,
  employmentData, // New prop for transformed data
  isPrivateProfile = false,
  profileUserId = null,
  isAuthenticated = true,
  onRequireAuth = null,
  isViewingOwnProfile = false,
}) => {
  const dispatch = useDispatch();
  const { success: showSuccess, error: showError } = useToast();
  const [requestLoading, setRequestLoading] = useState(false);
  
  // Get sent profile view requests from Redux
  const sentProfileViewRequests = useSelector((state) => state.user?.sentProfileViewRequests || []);

  // Fetch sent requests on mount if authenticated
  useEffect(() => {
    if (isAuthenticated && profileUserId && !isViewingOwnProfile) {
      dispatch(getSentProfileViewRequestsAction(null, 25, 0));
    }
  }, [dispatch, isAuthenticated, profileUserId, isViewingOwnProfile]);

  // Find existing profile view request for this profile owner
  const existingProfileViewRequest = useMemo(() => {
    if (!profileUserId || !sentProfileViewRequests || sentProfileViewRequests.length === 0 || isViewingOwnProfile) {
      return null;
    }
    
    const found = sentProfileViewRequests.find((request) => {
      const profileOwnerId = request.profileOwner?._id || request.profileOwner;
      return profileOwnerId && String(profileOwnerId) === String(profileUserId) && request.status === "pending";
    });
    
    return found;
  }, [sentProfileViewRequests, profileUserId, isViewingOwnProfile]);

  const handleRequestAccess = async () => {
    if (!isAuthenticated) {
      if (onRequireAuth) {
        onRequireAuth("auth_required");
      }
      return;
    }

    if (!profileUserId || isViewingOwnProfile) return;

    setRequestLoading(true);
    try {
      const result = await dispatch(createProfileViewRequestAction(profileUserId));
      if (result?.success) {
        showSuccess("Profile view request sent successfully");
        // Refresh sent requests to update button state
        await dispatch(getSentProfileViewRequestsAction(null, 25, 0));
      } else {
        showError(result?.error || "Failed to send request");
      }
    } catch (error) {
      showError("Error sending request");
    } finally {
      setRequestLoading(false);
    }
  };
  // Create a dynamic accordion state management
  const [accordionStates, setAccordionStates] = React.useState({});
  
  // Create expandable cards state management
  const [expandedCards, setExpandedCards] = React.useState({});
  
  
  // Function to toggle accordion state - only one can be open at a time
  const toggleAccordion = (organization) => {
    setAccordionStates(prev => {
      const newState = {};
      // Close all accordions first
      Object.keys(prev).forEach(key => {
        newState[key] = false;
      });
      // Open only the clicked one if it wasn't already open
      const isOpening = !prev[organization];
      newState[organization] = isOpening;
      
      // If opening an accordion, select the first project but don't expand it
      // For employments without projects, select the employment itself
      if (isOpening) {
        const employment = employmentList.find(emp => emp.organization === organization);
        if (employment?.projects?.length > 0) {
          const firstProjectId = employment.projects[0].id;
          setSelectedExperience(firstProjectId);
          setIsDescriptionVisible(false);
          // Reset all expanded cards - start with all collapsed
          setExpandedCards({});
        } else {
          // For employments without projects, use the employment ID as selectedExperience
          setSelectedExperience(employment.id);
          setIsDescriptionVisible(false);
          setExpandedCards({});
        }
      }
      
      return newState;
    });
  };
  
  // Function to get accordion state
  const getAccordionState = (organization) => {
    return accordionStates[organization] || false;
  };

  // Function to toggle card expansion - only one card can be open at a time
  const toggleCardExpansion = (cardId) => {
    setExpandedCards(prev => {
      const newState = {};
      // Close all cards first
      Object.keys(prev).forEach(key => {
        newState[key] = false;
      });
      // Open only the clicked card if it wasn't already open
      const isOpening = !prev[cardId];
      newState[cardId] = isOpening;
      return newState;
    });
  };

  // Function to get card expansion state
  const isCardExpanded = (cardId) => {
    return expandedCards[cardId] || false;
  };
  
  // Get employment data from API only - no hardcoded fallback
  const employmentList = React.useMemo(() => {
    const list = employmentData?.employments || [];
    // Sort employments by latest end date; also sort projects within each employment by end date
    return sortByEndDateDesc(list).map(sortNestedProjectsByEndDateDesc);
  }, [employmentData?.employments]);
  
  // Initialize accordion states when employment data changes
  React.useEffect(() => {
    if (employmentList.length > 0) {
      const initialStates = {};
      employmentList.forEach((employment, index) => {
        // Open first section by default
        initialStates[employment.organization] = index === 0;
      });
      
      setAccordionStates(initialStates);
      
      // Select the first project of the first employment by default but don't expand it
      // For employments without projects, select the employment itself
      const firstEmployment = employmentList[0];
      if (firstEmployment?.projects?.length > 0) {
        const firstProjectId = firstEmployment.projects[0].id;
        setSelectedExperience(firstProjectId);
        setExpandedCards({});
      } else {
        // For employments without projects, use the employment ID as selectedExperience
        setSelectedExperience(firstEmployment.id);
        setExpandedCards({});
      }
    }
  }, [employmentList, setSelectedExperience]);

  if (isPrivateProfile) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
        {/* Left - Employment list area */}
        <div className="col-span-1 bg-white rounded-lg border border-gray-200 shadow-sm p-6 flex flex-col items-center justify-center text-center">
          <svg
            className="w-8 h-8 text-amber-500 mb-3"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 11c-1.105 0-2 .895-2 2v3a2 2 0 104 0v-3a2 2 0 00-2-2z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M17 11V9a5 5 0 10-10 0v2M7 11h10a2 2 0 012 2v4a2 2 0 01-2 2H7a2 2 0 01-2-2v-4a2 2 0 012-2z"
            />
          </svg>
          <h3 className="text-base md:text-lg font-bold text-gray-900 font-heading mb-1">
            Private account
          </h3>
          <p className="text-xs md:text-sm text-gray-600 font-roboto-flex mb-3">
            This is a private account.
          </p>
          {!isViewingOwnProfile && (
            <button
              onClick={handleRequestAccess}
              disabled={requestLoading || existingProfileViewRequest}
              className={`w-full px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                existingProfileViewRequest
                  ? "bg-gray-100 text-gray-500 cursor-not-allowed"
                  : "bg-primary-700 text-white hover:bg-primary-800 disabled:opacity-50 disabled:cursor-not-allowed"
              }`}
            >
              {requestLoading ? (
                "Sending..."
              ) : existingProfileViewRequest ? (
                "Request Pending"
              ) : (
                "Request MYST Access"
              )}
            </button>
          )}
        </div>

        {/* Right - Detailed employment content */}
        <div className="col-span-1 md:col-span-3 bg-white rounded-lg border border-gray-200 shadow-sm p-8 flex flex-col items-center justify-center text-center">
          <svg
            className="w-10 h-10 text-amber-500 mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 11c-1.105 0-2 .895-2 2v3a2 2 0 104 0v-3a2 2 0 00-2-2z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M17 11V9a5 5 0 10-10 0v2M7 11h10a2 2 0 012 2v4a2 2 0 01-2 2H7a2 2 0 01-2-2v-4a2 2 0 012-2z"
            />
          </svg>
          <h2 className="text-lg md:text-xl font-bold text-gray-900 font-heading mb-2">
            Private account
          </h2>
          <p className="text-sm text-gray-600 font-roboto-flex max-w-md">
            This is a private account.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
      {/* Left Card - Experience List */}
      <div className="col-span-1 bg-white rounded-lg border border-gray-200 shadow-sm p-3 md:p-3 accordion-section">
        <div className="flex items-center gap-2 mb-2">
          <h3 className="text-base md:text-lg font-bold text-gray-900 font-heading">
            Employment
          </h3>
          {isPrivateProfile && (
            <svg
              className="w-4 h-4 text-amber-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 11c-1.105 0-2 .895-2 2v3a2 2 0 104 0v-3a2 2 0 00-2-2z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 11V9a5 5 0 10-10 0v2M7 11h10a2 2 0 012 2v4a2 2 0 01-2 2H7a2 2 0 01-2-2v-4a2 2 0 012-2z"
              />
            </svg>
          )}
        </div>
        
        {/* Dynamic Employment List */}
        {employmentList.map((employment, index) => {
          const isExpanded = getAccordionState(employment.organization);
          
          return (
            <div key={employment.organization} className="mb-2 accordion-list">
          <button
                onClick={() => toggleAccordion(employment.organization)}
            className={`accordion-button w-full rounded-md text-xs md:text-sm font-semibold text-gray-800 font-heading hover:bg-gray-50 transition-colors border-b border-gray-200 pb-2 ${
                  isExpanded ? 'expanded' : ''
                }`}
                aria-expanded={isExpanded}
                data-tooltip={employment.organization}
                title={employment.organization}
              >
                <span className="accordion-header">{employment.organization}</span>
            <svg
              className="w-4 h-4 text-gray-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
              {isExpanded && (
            <div className="space-y-2 mt-2">
                  {employment.projects?.map((project, projectIndex) => {
                    // Create a unique experience ID for each project using the actual project ID
                    const experienceId = project.id || `${employment.organization.toLowerCase().replace(/\s+/g, '-')}-${projectIndex}`;
                    
                    return (
                <button
                        key={experienceId}
                        onClick={() => {
                          setSelectedExperience(experienceId);
                          toggleCardExpansion(experienceId);
                        }}
                  className={`w-full text-left py-1.5 px-2 rounded-md text-sm transition-all duration-200 font-body tooltip-item truncate-text ${
                          selectedExperience === experienceId
                      ? "text-primary font-bold"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                        data-tooltip={project.title}
                        title={project.title}
                >
                        {project.title}
                </button>
                    );
                  })}
            </div>
          )}
        </div>
          );
        })}
        </div>
        
      {/* Right Card - Employment Details */}
      <div className="col-span-1 md:col-span-3 space-y-3">
        {/* Show content for the selected employment */}
        {(() => {
          // Find which employment is currently selected
          // Check if selectedExperience is an employment ID or a project ID
          let selectedEmployment = employmentList.find(emp => emp.id === selectedExperience);
          
          // If not found as employment ID, try to find it as a project ID
          if (!selectedEmployment) {
            selectedEmployment = employmentList.find(emp => 
              emp.projects?.some(project => project.id === selectedExperience)
            );
          }
          
          if (!selectedEmployment) {
            return (
              <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
                <div className="text-center text-gray-500">
                  <p className="text-sm font-medium">Select an employment to view details</p>
        </div>
      </div>
            );
          }
          
          // Only try to find a project if selectedExperience is not the employment ID itself
          const selectedProject = selectedEmployment.id === selectedExperience 
            ? null 
            : selectedEmployment.projects?.find(project => project.id === selectedExperience);
      
          return (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-3">
              {/* Company Header with Verification Badge and Document */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <h3 className="text-lg font-bold text-gray-900 font-heading">
                    {selectedEmployment.organization}
                  </h3>
                  {Array.isArray(selectedEmployment.validationData) && selectedEmployment.validationData.length > 0 && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setVerificationOverlay({
                        show: true,
                        title: selectedEmployment.organization,
                        validationData: selectedEmployment.validationData || []
                      });
                    }}
                    className="cursor-pointer hover:scale-110 transition-transform duration-200"
                  >
                    <HiBadgeCheck className="w-5 h-5 text-blue-600" />
                  </button>
                  )}
                </div>
                {selectedProject?.attachmentExists && (
                  <Tooltip text="Attachment available on MYST">
                    <HiLink className="w-5 h-5 text-gray-600" />
                  </Tooltip>
                )}
              </div>
              
              {/* Job Title and Details */}
                <div className="mb-3">
                    <p className="text-base font-roboto-flex text-gray-600">
                      {selectedEmployment?.designation || ''} • {selectedEmployment?.empType || ''} • {selectedEmployment?.duration}
                    </p>
                </div>
              
              {/* Employment Description Toggle */}
              {(selectedEmployment?.description || selectedEmployment?.kpi) && (
                <div className="mb-4">
                  <button
                    onClick={() => setIsDescriptionVisible(!isDescriptionVisible)}
                    className="font-flex-regular text-sm text-blue-400 hover:text-blue-500 font-normal underline transition-colors"
                  >
                    {isDescriptionVisible ? 'Hide Description' : 'View Description'}
                  </button>
                </div>
              )}
              
              {/* Employment Description and KPI Section */}
              {isDescriptionVisible && (selectedEmployment?.description || selectedEmployment?.kpi) && (
                <div className="mb-4">
                   {selectedEmployment?.description && (
                     <RichTextContent 
                       content={selectedEmployment.description}
                       className="font-flex-regular text-sm text-gray-700 leading-relaxed mb-3"
                     />
                   )}
                   {selectedEmployment?.kpi && (
                     <RichTextContent 
                       content={`KPI: ${selectedEmployment.kpi}`}
                       className="font-flex-regular text-sm text-gray-700"
                     />
                   )}
                </div>
              )}
              
              {/* Work Details Section - Only show if there are projects */}
              {selectedEmployment?.projects && selectedEmployment.projects.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-flex-regular text-sm font-semibold text-gray-800 font-heading">Work Details</h4>
                    {/* <button
                      onClick={() => setIsWorkDetailsExpanded(!isWorkDetailsExpanded)}
                      className="flex items-center text-sm font-medium text-gray-600 hover:text-gray-800 transition-colors"
                    >
                      <svg
                        className={`w-4 h-4 transition-transform duration-200 ${isWorkDetailsExpanded ? 'rotate-90' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button> */}
                  </div>
                
                   {/* Project Cards Grid - 2 per row with expandable functionality */}
                   <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {selectedEmployment.projects?.map((project, index) => {
                    const isProjectExpanded = isCardExpanded(project.id);
                    const hasAnyExpanded = Object.values(expandedCards).some(expanded => expanded);
                    const shouldShow = !hasAnyExpanded || isProjectExpanded;
                    
                    if (!shouldShow) return null;
                    
                    return (
                      <div
                        key={project.id}
                        className={`bg-gray-50 rounded-lg border border-gray-200 cursor-pointer hover:shadow-md transition-all duration-300 ${
                          isProjectExpanded ? 'col-span-2' : ''
                        }`}
                        onClick={() => {
                          setSelectedExperience(project.id);
                          setIsDescriptionVisible(false);
                          toggleCardExpansion(project.id);
                        }}
                      >
                        <div className={`${isProjectExpanded ? 'p-4' : 'p-3'}`}>
                          <div className="flex items-start justify-between mb-2">
                            <h5 className={`font-semibold text-gray-900 font-heading leading-tight ${isProjectExpanded ? 'text-base' : 'text-sm'}`}>
                              {project.title}
                            </h5>
                            <div className="flex items-center gap-2">
                              {isProjectExpanded && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setExpandedCards({});
                                    setVerificationOverlay({ show: false });
                                  }}
                                  className="cursor-pointer hover:scale-110 transition-transform duration-200"
                                >
                                  <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </button>
                              )}
                              {isProjectExpanded && Array.isArray(project.validationData) && project.validationData.length > 0 && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setVerificationOverlay({
                                      show: true,
                                      title: project.title,
                                      validationData: project.validationData || []
                                    });
                                  }}
                                  className="cursor-pointer hover:scale-110 transition-transform duration-200"
                                >
                                  <HiBadgeCheck className="w-4 h-4 text-blue-600 flex-shrink-0" />
                                </button>
                              )}
                              {isProjectExpanded && project.attachmentExists && (
                                <Tooltip text="Attachment available on MYST">
                                  <HiLink className="w-4 h-4 text-gray-600" />
                                </Tooltip>
                              )}
                            </div>
                          </div>
                          <p className="font-flex-regular text-gray-600 text-xs mb-1">{project.role}</p>
                          <p className="font-flex-regular text-gray-500 text-xs mb-2">{project.location} {project.duration}</p>
                    
                          {isProjectExpanded && (
                            <div className="mt-3 space-y-2">
                               {project.description && (
                         <RichTextContent 
                           content={project.description}
                           className="font-flex-regular text-sm text-gray-700 leading-relaxed"
                         />
                               )}
                               {project.kpi && (
                                 <RichTextContent 
                                   content={`KPI: ${project.kpi}`}
                                   className="font-flex-regular text-sm text-gray-700"
                                 />
                               )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                    </div>
                </div>
              )}
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default EmploymentTab;
